import { useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Alert, Button, Dialog, Input, Select, Textarea } from "@/components/ui";
import type { UserRoleType } from "../types";
import { formatLabel } from "../labels";

const ROLE_TYPES: UserRoleType[] = [
  "customer",
  "creator",
  "business_owner",
  "business_staff",
  "coach",
  "personal_trainer",
  "community_founder",
  "community_manager",
  "employee",
];

export interface CreateRoleExtensionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: { roleType: UserRoleType }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function CreateRoleExtensionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: CreateRoleExtensionDialogProps) {
  const [roleType, setRoleType] = useState<UserRoleType>("customer");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add role extension"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => void onSubmit({ roleType })}
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Role extensions remain separate from account lifecycle status.
        </Text>
        <Select
          label="Role type"
          value={roleType}
          onChange={(event) => setRoleType(event.target.value as UserRoleType)}
          options={ROLE_TYPES.map((role) => ({
            value: role,
            label: formatLabel(role),
          }))}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export interface RoleExtensionActionDialogProps {
  open: boolean;
  onClose: () => void;
  actionLabel: string;
  onSubmit: (reason: string) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}

export function RoleExtensionActionDialog({
  open,
  onClose,
  actionLabel,
  onSubmit,
  busy = false,
  error = null,
}: RoleExtensionActionDialogProps) {
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${formatLabel(actionLabel)} role extension`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() => void onSubmit(reason)}
          >
            Confirm
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
        <Input label="Action" value={actionLabel} disabled readOnly />
      </Stack>
    </Dialog>
  );
}
